import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Loader2, Search, X, RotateCcw, ShieldCheck, AlertTriangle, Pencil } from 'lucide-react';
import {
  dynamicSupplyReview,
  dynamicSupplyEdit,
  dynamicSupplyEditRevert,
  dynamicSupplyEditRevertAll,
} from '../api';

const numFmt = (n) => (n == null ? '' : Number(n).toLocaleString('en-US'));

/**
 * Manual Review + Edit modal shown BEFORE final import commit.
 *
 * • Search across school name / ministerial / item / education admin
 * • Quick filters: missing ministerial · missing coords · edited · qty/price
 * • Inline edit for school identity, item, quantity, unit price, coords
 * • Every edit records the original value + timestamp (audit trail)
 * • Live recalculation of per-item and grand totals as the user types
 * • Revert single-cell or every-edit
 */
export default function ManualReviewModal({ token, supplier, onClose, onCommitReady }) {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [recon, setRecon] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all|missing_id|missing_coords|edited
  const [savingKey, setSavingKey] = useState(null);
  const [totalEdits, setTotalEdits] = useState(0);
  const [reviewedManually, setReviewedManually] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await dynamicSupplyReview({ token, supplier });
      setRecords(d.records || []);
      setRecon(d.reconciliation);
      setTotal(d.total_records || 0);
      setTotalEdits(d.edits_applied || 0);
      if (d.edits_applied > 0) setReviewedManually(true);
    } finally { setLoading(false); }
  }, [token, supplier]);

  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return records.filter((r) => {
      if (filter === 'missing_id' && !r.school_id_missing) return false;
      if (filter === 'missing_coords' && r.coords) return false;
      if (filter === 'edited' && !r._edited) return false;
      if (!query) return true;
      return [r.school_name, r.school_id, r.item, r.governorate, r.region]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(query));
    });
  }, [records, q, filter]);

  // Live totals — client-side reconciliation of the edited dataset.
  const liveTotals = useMemo(() => {
    let qty = 0, val = 0, editedCount = 0;
    for (const r of records) {
      const q_ = Number(r.quantity) || 0;
      const up = Number(r.unit_price) || 0;
      qty += q_;
      val += q_ * up;
      if (r._edited) editedCount += 1;
    }
    return { qty, val, editedCount };
  }, [records]);

  const applyEdit = async (r, field, val) => {
    // Coerce numeric strings
    let coerced = val;
    if (field === 'quantity' || field === 'unit_price') {
      coerced = val === '' ? null : Number(val);
      if (Number.isNaN(coerced)) return;
    }
    setSavingKey(`${r.review_key}:${field}`);
    try {
      await dynamicSupplyEdit({ token, review_key: r.review_key, field, value: coerced });
      setRecords((prev) => prev.map((row) => {
        if (row.review_key !== r.review_key) return row;
        const original = row._original || {};
        if (!(field in original)) original[field] = row[field];
        return {
          ...row,
          [field]: coerced,
          _original: original,
          _edited: true,
          total_value: field === 'quantity' || field === 'unit_price'
            ? (Number(field === 'quantity' ? coerced : row.quantity) || 0) *
              (Number(field === 'unit_price' ? coerced : row.unit_price) || 0)
            : row.total_value,
        };
      }));
      setTotalEdits((n) => n + 1);
      setReviewedManually(true);
    } finally { setSavingKey(null); }
  };

  const revertField = async (r, field) => {
    setSavingKey(`${r.review_key}:${field}:revert`);
    try {
      await dynamicSupplyEditRevert({ token, review_key: r.review_key, field });
      setRecords((prev) => prev.map((row) => {
        if (row.review_key !== r.review_key) return row;
        const orig = { ...(row._original || {}) };
        const origVal = orig[field];
        delete orig[field];
        return {
          ...row,
          [field]: origVal,
          _original: Object.keys(orig).length ? orig : undefined,
          _edited: Object.keys(orig).length > 0,
          total_value:
            (Number(field === 'quantity' ? origVal : row.quantity) || 0) *
            (Number(field === 'unit_price' ? origVal : row.unit_price) || 0),
        };
      }));
      setTotalEdits((n) => Math.max(0, n - 1));
    } finally { setSavingKey(null); }
  };

  const revertAll = async () => {
    if (!window.confirm('إعادة جميع القيم للأصل؟ سيتم فقد جميع التعديلات اليدوية.')) return;
    await dynamicSupplyEditRevertAll({ token });
    await refresh();
    setReviewedManually(false);
  };

  const statusLabel = reviewedManually
    ? 'مطابق بعد المراجعة اليدوية'
    : (recon?.status === 'MATCHED' ? 'مطابق'
       : recon?.status === 'MISMATCH' ? 'اختلاف'
       : recon?.status === 'ERROR' ? 'خطأ' : 'تحذير');
  const statusTone = recon?.status === 'MATCHED' && !reviewedManually
    ? { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' }
    : reviewedManually
      ? { bg: '#EDE9FE', text: '#5B21B6', border: '#C4B5FD' }
      : recon?.status === 'MISMATCH'
        ? { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' }
        : { bg: '#FEF3C7', text: '#92400E', border: '#FCD34D' };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: 'rgba(6,5,15,0.85)', backdropFilter: 'blur(6px)' }} onClick={onClose}>
      <div dir="rtl" onClick={(e) => e.stopPropagation()}
           data-testid="manual-review-modal"
           className="w-[min(1300px,96vw)] h-[92vh] rounded-2xl overflow-hidden flex flex-col"
           style={{ background: '#0F0E19', border: '1px solid rgba(255,255,255,0.08)' }}>
        {/* Header */}
        <div className="px-5 py-4 flex items-center justify-between border-b border-white/10">
          <div>
            <div className="text-[11px] font-semibold tracking-widest" style={{ color: '#9D8CFF' }}>MANUAL REVIEW · PRE-COMMIT</div>
            <div className="text-[18px] font-bold mt-1" style={{ color: '#F1EEFB' }}>مراجعة وتعديل البيانات — {supplier}</div>
          </div>
          <div className="flex items-center gap-2">
            <div className="px-3 py-1.5 rounded-lg text-[11.5px] font-bold"
                 style={{ background: statusTone.bg, color: statusTone.text, border: `1px solid ${statusTone.border}` }}
                 data-testid="review-status-pill">
              {statusLabel}
            </div>
            <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-white/5" style={{ color: '#F1EEFB' }} data-testid="review-close">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Live totals strip */}
        <div className="px-5 py-3 grid grid-cols-4 gap-3 border-b border-white/5" style={{ background: '#171528' }}>
          <Live label="السجلات المعروضة" value={numFmt(filtered.length)} sub={`من ${numFmt(total)}`} />
          <Live label="الكمية المحسوبة" value={numFmt(liveTotals.qty)} tint="#9D8CFF" />
          <Live label="القيمة المحسوبة (ر.س)" value={numFmt(Math.round(liveTotals.val))} tint="#38D6C7" />
          <Live label="سجلات معدّلة" value={numFmt(liveTotals.editedCount)} tint="#F59E0B" />
        </div>

        {/* Filters */}
        <div className="px-5 py-3 flex items-center gap-2 border-b border-white/5">
          <div className="relative flex-1 max-w-[420px]">
            <Search size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="review-search"
                   value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="بحث بالمدرسة / الرقم / الصنف / الإدارة..."
                   className="w-full pr-8 pl-3 py-2 rounded-lg text-[12px] outline-none"
                   style={{ background: '#0B0A15', border: '1px solid rgba(255,255,255,0.08)', color: '#F1EEFB' }} />
          </div>
          {[
            { k: 'all', label: 'الكل' },
            { k: 'missing_id', label: 'بلا رقم وزاري' },
            { k: 'missing_coords', label: 'بلا إحداثيات' },
            { k: 'edited', label: 'معدّلة' },
          ].map((f) => (
            <button key={f.k} onClick={() => setFilter(f.k)}
                    data-testid={`review-filter-${f.k}`}
                    className="px-3 py-1.5 rounded-lg text-[11.5px] font-semibold transition-colors"
                    style={filter === f.k
                      ? { background: 'rgba(109,93,251,0.25)', border: '1px solid rgba(109,93,251,0.6)', color: '#F1EEFB' }
                      : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#B7B5C8' }}>
              {f.label}
            </button>
          ))}
          <div className="mr-auto flex items-center gap-2">
            <button data-testid="review-revert-all" onClick={revertAll}
                    disabled={liveTotals.editedCount === 0}
                    className="px-3 py-1.5 rounded-lg text-[11.5px] font-semibold flex items-center gap-1 disabled:opacity-40"
                    style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.35)', color: '#FCA5A5' }}>
              <RotateCcw size={12} /> إعادة الكل للأصل
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto" style={{ background: '#0B0A15' }}>
          {loading ? (
            <div className="p-14 text-center"><Loader2 size={28} className="animate-spin mx-auto" style={{ color: '#9D8CFF' }} /></div>
          ) : filtered.length === 0 ? (
            <div className="p-14 text-center text-slate-500 text-[12px]">لا توجد سجلات مطابقة</div>
          ) : (
            <table className="w-full text-[11.5px]" data-testid="review-table">
              <thead className="sticky top-0" style={{ background: '#12111E', color: '#B7B5C8' }}>
                <tr>
                  <Th>المدرسة</Th>
                  <Th>الرقم الوزاري</Th>
                  <Th>إدارة التعليم</Th>
                  <Th>المدينة</Th>
                  <Th>المورد</Th>
                  <Th>الصنف</Th>
                  <Th center>الكمية</Th>
                  <Th center>سعر الوحدة</Th>
                  <Th center>القيمة</Th>
                  <Th center>الحالة</Th>
                  <Th>Lat</Th>
                  <Th>Lng</Th>
                  <Th center width={70}>—</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 500).map((r) => (
                  <Row key={r.review_key} r={r}
                       onEdit={applyEdit} onRevertField={revertField} savingKey={savingKey} />
                ))}
                {filtered.length > 500 && (
                  <tr><td colSpan={13} className="text-center py-3 text-slate-500 text-[11px]">
                    عرض 500 من {numFmt(filtered.length)} — استخدم البحث أو الفلاتر لتضييق النتائج
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 flex items-center gap-2 border-t border-white/10">
          <div className="text-[11px] text-slate-400">
            {liveTotals.editedCount > 0 ? <>معدّل: <b style={{ color: '#F59E0B' }}>{numFmt(liveTotals.editedCount)}</b> سجل — سيتم حفظ التعديلات مع الاستيراد النهائي.</> : 'لم يتم تعديل أي سجل بعد.'}
          </div>
          <button data-testid="review-cancel" onClick={onClose} className="mr-auto px-4 py-2 rounded-lg text-[12.5px] font-semibold"
                  style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', color: '#F1EEFB' }}>
            إلغاء
          </button>
          <button data-testid="review-confirm" onClick={() => { onCommitReady?.(liveTotals); onClose(); }}
                  className="px-4 py-2 rounded-lg text-[12.5px] font-bold flex items-center gap-2"
                  style={{ background: 'linear-gradient(135deg,#6D5DFB,#7C6FFF)', color: '#fff' }}>
            <ShieldCheck size={14} /> إعتماد المراجعة والعودة للتأكيد
          </button>
        </div>
      </div>
    </div>
  );
}

function Th({ children, center, width }) {
  return <th className={`px-2 py-2 text-[10.5px] font-semibold uppercase tracking-wider ${center ? 'text-center' : 'text-right'}`} style={{ width }}>{children}</th>;
}

function Live({ label, value, sub, tint = '#F1EEFB' }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="text-[18px] font-bold tabular-nums mt-0.5" style={{ color: tint }}>
        {value} {sub && <span className="text-slate-500 text-[11px] font-normal mr-1">{sub}</span>}
      </div>
    </div>
  );
}

function Row({ r, onEdit, onRevertField, savingKey }) {
  const missingId = r.school_id_missing;
  const editedFields = new Set(Object.keys(r._original || {}));
  const lat = r.coords ? (r.coords.split(',')[0] || '').trim() : '';
  const lng = r.coords ? (r.coords.split(',')[1] || '').trim() : '';
  return (
    <tr className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`review-row-${r.review_key}`}>
      <Cell edited={editedFields.has('school_name')}>
        <Inline value={r.school_name || ''} onSave={(v) => onEdit(r, 'school_name', v)} testid="cell-school-name" />
      </Cell>
      <Cell edited={editedFields.has('school_id')}>
        {missingId
          ? <span style={{ color: '#F59E0B' }} className="text-[10.5px]">غير متوفر</span>
          : <Inline value={r.school_id || ''} onSave={(v) => onEdit(r, 'school_id', v)} testid="cell-school-id" />}
      </Cell>
      <Cell edited={editedFields.has('governorate')}>
        <Inline value={r.governorate || ''} onSave={(v) => onEdit(r, 'governorate', v)} testid="cell-gov" />
      </Cell>
      <Cell edited={editedFields.has('region')}>
        <Inline value={r.region || ''} onSave={(v) => onEdit(r, 'region', v)} testid="cell-region" />
      </Cell>
      <Cell edited={editedFields.has('company')}>
        <Inline value={r.company || ''} onSave={(v) => onEdit(r, 'company', v)} testid="cell-company" />
      </Cell>
      <Cell edited={editedFields.has('item')}>
        <Inline value={r.item || ''} onSave={(v) => onEdit(r, 'item', v)} testid="cell-item" />
      </Cell>
      <Cell edited={editedFields.has('quantity')} center>
        <InlineNum value={r.quantity ?? ''} onSave={(v) => onEdit(r, 'quantity', v)} testid="cell-qty" />
      </Cell>
      <Cell edited={editedFields.has('unit_price')} center>
        <InlineNum value={r.unit_price ?? ''} onSave={(v) => onEdit(r, 'unit_price', v)} testid="cell-price" />
      </Cell>
      <Cell center>
        <span className="tabular-nums" style={{ color: '#38D6C7' }}>{numFmt(Math.round((Number(r.quantity) || 0) * (Number(r.unit_price) || 0)))}</span>
      </Cell>
      <Cell edited={editedFields.has('status')} center>
        <StatusSelect value={r.status || 'pending'} onSave={(v) => onEdit(r, 'status', v)} testid="cell-status" />
      </Cell>
      <Cell edited={editedFields.has('latitude') || editedFields.has('coords')}>
        <Inline value={lat} onSave={(v) => onEdit(r, 'latitude', v)} testid="cell-lat" placeholder="—" />
      </Cell>
      <Cell edited={editedFields.has('longitude') || editedFields.has('coords')}>
        <Inline value={lng} onSave={(v) => onEdit(r, 'longitude', v)} testid="cell-lng" placeholder="—" />
      </Cell>
      <Cell center>
        {r._edited && (
          <button onClick={() => onRevertField(r, [...editedFields][0])} data-testid="cell-revert"
                  className="text-[10px] px-2 py-1 rounded" style={{ color: '#FCA5A5' }}>
            <RotateCcw size={11} className="inline-block ml-1" /> تراجع
          </button>
        )}
      </Cell>
    </tr>
  );
}

function Cell({ children, edited, center }) {
  return (
    <td className={`px-2 py-1.5 ${center ? 'text-center' : ''}`}
        style={edited ? { background: 'rgba(245,158,11,0.12)', borderLeft: '2px solid #F59E0B' } : undefined}>
      {children}
    </td>
  );
}

function Inline({ value, onSave, testid, placeholder = '' }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  return (
    <input data-testid={testid}
           value={v} onChange={(e) => setV(e.target.value)}
           onBlur={() => { if (v !== value) onSave(v); }}
           onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
           placeholder={placeholder}
           className="bg-transparent outline-none w-full text-right"
           style={{ color: '#F1EEFB' }} />
  );
}

function InlineNum({ value, onSave, testid }) {
  const [v, setV] = useState(value ?? '');
  useEffect(() => setV(value ?? ''), [value]);
  return (
    <input data-testid={testid}
           value={v} onChange={(e) => setV(e.target.value)}
           onBlur={() => { if (String(v) !== String(value)) onSave(v); }}
           onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
           className="bg-transparent outline-none w-24 text-center tabular-nums"
           style={{ color: '#F1EEFB' }} />
  );
}

function StatusSelect({ value, onSave, testid }) {
  return (
    <select data-testid={testid}
            value={value || 'pending'}
            onChange={(e) => onSave(e.target.value)}
            className="text-[11px] rounded outline-none"
            style={{ background: '#FFFFFF', color: '#1E293B',
                     border: '1px solid rgba(0,0,0,0.15)', padding: '3px 6px' }}>
      <option value="delivered">تم التوريد</option>
      <option value="pending">قيد التوريد</option>
      <option value="delayed">متأخر</option>
    </select>
  );
}
